import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn, useBudgets, useGetUserApi } from '@liquidbudget/common';
import {
  CloudDownloadIcon,
  DropletsIcon,
  GraduationCapIcon,
  Grid2x2CheckIcon,
  LogOutIcon,
  ShieldHalfIcon,
  UserIcon,
} from 'lucide-react';
import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BudgetSwitcher } from '@/components/budget-switcher/budget-switcher';
import { Dialog } from '@/components/ui/dialog';
import { LogoutDialogContent } from '@/components/logout/logout-dialog-content';

interface Props {
  children: ReactNode;
}

export const DashboardSidebar = ({ children }: Props) => {
  const { data: user } = useGetUserApi();
  const budgets = useBudgets();
  const isAdmin = user?.role === 'ADMIN';
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  return (
    <SidebarProvider>
      <Sidebar>
        <SidebarHeader className="flex h-14 items-center px-4 border-b border-sidebar-border">
          <BudgetSwitcher />
        </SidebarHeader>
        <SidebarContent>
          <div className="px-4 pt-4 pb-2">
            <h2 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">Budgets</h2>
          </div>

          <nav className="grid items-start px-2 text-sm font-medium lg:px-4 gap-1">
            <NavLink to="/dashboard/budget-manager" icon={<Grid2x2CheckIcon className="h-4 w-4 mr-2" />}>
              Budget Manager
            </NavLink>

            {budgets.map((budget) => (
              <NavLink key={budget.id} to={`/budget/${budget.id}`} icon={<DropletsIcon className="h-4 w-4 mr-2" />}>
                {budget.name}
              </NavLink>
            ))}
          </nav>

          {isAdmin && (
            <>
              <div className="px-4 pt-4 pb-2">
                <h2 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">Administration</h2>
              </div>
              <nav className="grid items-start px-2 text-sm font-medium lg:px-4 gap-1 py-2">
                <NavLink to="/admin/users" icon={<ShieldHalfIcon className="h-4 w-4 mr-2" />}>
                  Users
                </NavLink>
                <NavLink to="/admin/subscriptions" icon={<ShieldHalfIcon className="h-4 w-4 mr-2" />}>
                  Subscriptions
                </NavLink>
              </nav>
            </>
          )}

          <div className="px-4 pt-4 pb-2">
            <h2 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">Settings</h2>
          </div>

          <nav className="grid items-start px-2 text-sm font-medium lg:px-4 gap-1 py-2">
            <NavLink to="/dashboard/account-settings" icon={<UserIcon className="h-4 w-4 mr-2" />}>
              Account Settings
            </NavLink>
            <NavLink to="/dashboard/bank-integration" icon={<CloudDownloadIcon className="h-4 w-4 mr-2" />}>
              Bank Integration
            </NavLink>
            <NavLink to="/dashboard/tutorial" icon={<GraduationCapIcon className="h-4 w-4 mr-2" />}>
              Tutorial
            </NavLink>

            <LogoutButton onClick={() => setLogoutDialogOpen(true)} />
          </nav>
        </SidebarContent>
      </Sidebar>
      <SidebarInset>{children}</SidebarInset>

      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <LogoutDialogContent onClose={() => setLogoutDialogOpen(false)} />
      </Dialog>
    </SidebarProvider>
  );
};

interface NavLinkProps {
  to: string;
  icon?: ReactNode;
  children: ReactNode;
}

export function NavLink({ to, icon, children }: NavLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === to;
  const { setOpenMobile } = useSidebar();

  const handleClick = () => {
    setOpenMobile(false);
  };

  return (
    <Link
      to={to}
      className={cn(
        'flex items-center w-full px-2 py-2 rounded-md',
        isActive ? 'bg-primary text-primary-foreground shadow hover:bg-primary/90' : 'hover:bg-muted',
      )}
      onClick={handleClick}
    >
      {icon}
      <span>{children}</span>
    </Link>
  );
}

interface LogoutButtonProps {
  onClick: () => void;
}

function LogoutButton({ onClick }: LogoutButtonProps) {
  const { setOpenMobile } = useSidebar();

  const handleClick = () => {
    setOpenMobile(false);
    onClick();
  };

  return (
    <button
      onClick={handleClick}
      className={cn('flex items-center w-full px-2 py-2 rounded-md text-left', 'hover:bg-muted')}
    >
      <LogOutIcon className="h-4 w-4 mr-2" />
      <span>Logout</span>
    </button>
  );
}
